import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { userPreferencesService } from './userPreferences.service';
import { CLEAR_TAB_FILTERS, GET_USER_PREFERENCES, GET_USER_PREFERENCES_SUCCESS, SAVE_TAB_FILTERS } from './userPreferences.type';

/**
 * Bootstrap read. One call, every tab.
 *
 * No loading/error actions on purpose: a failure here is not something the user
 * can act on, and painting an error for it would interrupt a session over UI
 * state. A failed read degrades to "no saved filters" — the grid still renders,
 * just unfiltered — so the success action fires either way to release the gate.
 */
function* getUserPreferences() {
    try {
        const res = yield call(userPreferencesService.getPreferencesService);
        yield put({
            type: GET_USER_PREFERENCES_SUCCESS,
            payload: res.data,
        });
    } catch (err) {
        yield put({
            type: GET_USER_PREFERENCES_SUCCESS,
            payload: { filters: {} },
        });
    }
}

/**
 * Fire-and-forget write.
 *
 * The filters are already applied locally and already in the store — this only
 * mirrors them to the server. It deliberately raises no loading state and no
 * error toast: the user never asked for a save, so they must never wait on one
 * or be told one failed.
 *
 * `takeEvery`, not `takeLatest`: writes for different tabs are independent, and
 * same-tab bursts are already collapsed by the debounce at the call site.
 */
function* saveTabFilters({ payload }) {
    try {
        yield call(userPreferencesService.saveTabFiltersService, payload.tab, payload.filters);
    } catch (err) {
        // Swallowed by design — see above.
    }
}

function* clearTabFilters({ payload }) {
    try {
        yield call(userPreferencesService.clearTabFiltersService, payload.tab);
    } catch (err) {
        // Swallowed by design — see above.
    }
}

export const watchUserPreferences = function* watchUserPreferencesSaga() {
    yield takeLatest(GET_USER_PREFERENCES, getUserPreferences);
    yield takeEvery(SAVE_TAB_FILTERS, saveTabFilters);
    yield takeEvery(CLEAR_TAB_FILTERS, clearTabFilters);
};
